import { clearCache, useRequest, useUpdateEffect } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';

import { useI18nContext } from '@/i18n/I18n';
import { IMemoUserInfo } from '@/pages/security/types';
import { clearFetchCache } from '@/services/request';
import { getAvatarInfo, getUserProfile } from '@/services/security';
import { IBaseResponse } from '@/services/types/common';
import { UserInfo } from '@/services/types/common';
import { isUnAuthPage } from '@/utils/loginAuth';
import { VipStatus } from '@/utils/pageConfig';
import redirectUrlMap from '@/utils/redirectUrlMap';

const CACHE_KEY = 'userInfo';
let uid = 0;
// 绑定代理商有效期，注册14天内可以绑定
const BIND_VALIDAITY_PERIOD = 14 * 24 * 60 * 60 * 1000;

type mutateType =
  | IBaseResponse<UserInfo>
  | ((
      oldData?: IBaseResponse<UserInfo>,
    ) => IBaseResponse<UserInfo> | undefined);

export function useUserProfile() {
  const {
    data: profile,
    error,
    loading,
    refresh,
    mutate: mutateUserInfo,
  } = useRequest<IBaseResponse<UserInfo>, []>(getUserProfile, {
    cacheKey: CACHE_KEY,
    staleTime: -1, // data is considered fresh so we call this api only once
    cacheTime: -1, // never clear cache so we call this api only once
    onError: () => {
      // 判断是否跳过未登录状态拦截
      handleNotLoginAuth();
    },
  });

  const handleNotLoginAuth = () => {
    if (!isUnAuthPage()) {
      redirectUrlMap.redirectLogin();
    }
  };

  const userInfo = useMemo(() => {
    return profile?.result || {};
  }, [profile?.result]) as UserInfo;

  const isSubmember = useMemo(() => {
    return userInfo?.is_submember;
  }, [userInfo]);

  const isDemoAccount = useMemo(() => {
    return userInfo?.demo_id > 0;
  }, [userInfo]);

  const isIslamicAccount = useMemo(() => {
    return userInfo?.member_tag?.is_islamic === '1';
  }, [userInfo]);

  const grayFlag = useMemo(() => {
    return userInfo?.grayFlag;
  }, [userInfo]);

  const isUta = useMemo(() => {
    return (
      userInfo?.member_tag?.UTA === 'SUCCESS' ||
      userInfo?.member_tag?.UTA === 'PROCESS'
    );
  }, [userInfo]);

  const isCopyTrader = useMemo(() => {
    return userInfo?.member_tag?.copytrade_upgrade;
  }, [userInfo]);

  const is_copy_pro_sub_account = useMemo(() => {
    return userInfo?.member_tag?.member_type_copypro === '1';
  }, [userInfo]);

  const currencyCode = useMemo(() => {
    const currencyCode = (userInfo as UserInfo)?.currency_code || 'USD';

    return currencyCode;
  }, [userInfo]);
  const id = useMemo(() => {
    return (userInfo as UserInfo)?.id || 0;
  }, [userInfo]);

  const isUTA2 = useMemo(() => {
    return userInfo?.member_tag?.UTA_INVERSE === 'SUCCESS';
  }, [userInfo]);

  const mutate = (callback: mutateType) => {
    mutateUserInfo(callback);
    uid = 0;
  };
  const onSuccess = () => {
    uid = id;
    window.GA_UID = id;
  };

  useUpdateEffect(() => {
    if (id > 0 && uid !== id && !loading) {
      onSuccess();
    } else if (!loading && id === 0) {
      handleNotLoginAuth();
    }
  }, [profile?.result]);

  const hasGoogle2fa = useMemo(
    () => (userInfo as UserInfo)?.has_google2fa,
    [userInfo],
  );

  // 是否为正式Vip
  const isOfficialVip = useMemo(
    () =>
      (userInfo as UserInfo)?.loyalty_vip_info?.vip_status ===
      VipStatus.VipStatus_VIP_STATUS_REGULAR,
    [userInfo],
  );

  // 是否为Pro用户
  const isVipPro = useMemo(
    () =>
      (userInfo as UserInfo)?.loyalty_vip_info?.vip_status ===
      VipStatus.VipStatus_VIP_STATUS_PRO,
    [userInfo],
  );

  const isRegisteOverTime = useMemo(() => {
    const { created_at } = userInfo;
    const registerTime = created_at?.replace(' ', 'T')?.concat('Z');
    console.log(registerTime, 'registerTime');
    // 注册日期是否超过14天
    return +new Date() - +new Date(registerTime) > BIND_VALIDAITY_PERIOD;
  }, [userInfo]);

  const accountIdentity = useMemo(() => {
    const identity = [];
    if (userInfo?.is_submember) {
      identity.push('submember');
    }
    if (userInfo?.member_tag?.UTA === 'SUCCESS') {
      identity.push('uta');
    }
    if (userInfo?.member_tag?.UTA_INVERSE === 'SUCCESS') {
      identity.push('uta_inverse');
    }
    if (isOfficialVip || isVipPro) {
      identity.push('regular_vip');
    }
    if (userInfo?.demo_id > 0) {
      identity.push('demo_account');
    }
    if (isIslamicAccount) {
      identity.push('islamicAccount');
    }
    return identity;
  }, [userInfo, isOfficialVip, isIslamicAccount, isVipPro]);

  const updateUserProfile = (
    cb?: (profile: IBaseResponse<UserInfo>) => void,
  ) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        '%cupdateUserProfile called. you can get result data from your callback function input parameters',
        'color: green',
      );
    }
    clearCache(CACHE_KEY);
    clearFetchCache('userProfile');
    refresh();
    cb && cb(profile as IBaseResponse<UserInfo>);
  };

  return {
    id,
    isRegisteOverTime,
    currencyCode,
    error,
    hasGoogle2fa,
    loading,
    profile,
    userInfo,
    isSubmember,
    mutate,
    updateUserProfile,
    isCopyTrader,
    isUta,
    isDemoAccount,
    is_copy_pro_sub_account,
    isIslamicAccount,
    grayFlag,
    isOfficialVip,
    isVipPro,
    isUTA2,
    accountIdentity,
  };
}

const COPY_TRADING_PRO = 9;

export function useAvatar(user: IMemoUserInfo) {
  const [$t] = useI18nContext();
  const [avatar, setAvatar] = useState({ avatar_url: '', avatar_id: 0 });
  const [copyTraderInfo, setCopyTraderInfo] = useState({ nick: '', photo: '' });
  const [copyTradingPro, setCopyTradingPro] = useState(false);
  useEffect(() => {
    if (!user.id) return;
    getAvatarInfo().then((res: any) => {
      const result = res.result;
      if (result !== null && result.avatar !== null) {
        setAvatar({
          avatar_id: result.avatar.avatarId || '',
          avatar_url: result.avatar.avatarUrl || '',
        });
      }
      if (result !== null && result.copytradeUpgrade === 'SUCCESS') {
        setCopyTraderInfo({
          nick: result.copytradeNick || $t('child.masterTrader'),
          photo: result.copytradePhoto || '',
        });
      }
      setCopyTradingPro(result.memberType === COPY_TRADING_PRO);
    });
  }, [user]);

  return {
    avatar,
    setAvatar,
    copyTraderInfo,
    copyTradingPro,
  };
}
